import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { EnhancedProductCard } from '../components/shared/EnhancedProductCard';
import { healthProducts } from '../data/products';
import { SparklesIcon, ArrowRightIcon, TagIcon } from 'lucide-react';
export function QuizResultsPage() {
  const [searchParams] = useSearchParams();
  const goalId = searchParams.get('goal');
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Simple mapping of goals to titles
  const goalTitles: Record<string, string> = {
    'build-muscle': 'Muscle Building',
    'weight-loss': 'Weight Loss',
    'increase-energy': 'Energy & Performance',
    'general-health': 'General Health',
    'womens-health': "Women's Health",
    'mens-health': "Men's Health"
  };
  const title = goalId ? goalTitles[goalId] || 'Your Custom' : 'Your Custom';
  // Mock recommendations based on goal (just taking first 4 products for demo)
  const recommendedProducts = healthProducts.slice(0, 4);
  return <HelmetProvider data-id="element-7903" data-mp="d8ny7f8b5q53">
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-7904" data-mp="d6bg922jvfs1">
        <SEO title="Your Personalized Plan | Innova Pharmacy" description="Your personalized supplement recommendations." data-id="element-7905" data-mp="h97ted9bmjol" />

        <div className="hidden md:block" data-id="element-7906" data-mp="1l73eoyzx8nhw">
          <AnnouncementBar data-id="element-7907" data-mp="1l61rxx9jmckc" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-7908" data-mp="1l9xxkm3ae097" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7909" data-mp="1c887bjl557d0" />
        </div>

        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7910" data-mp="204vrvkhzwzsx">
          <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-7911" data-mp="20318iamdhfqy" />
        </div>

        <div className="h-[112px] md:hidden" data-id="element-7912" data-mp="2sn9hwxx3bhhg"></div>

        <main className="flex-grow container mx-auto px-4 py-8 max-w-6xl pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7913" data-mp="2ntf0y28vdgm2">
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-2xl p-8 text-white text-center mb-12 relative overflow-hidden" data-id="element-7914" data-mp="2v7pez221pv7x">
            {/* Decorative elements */}
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white bg-opacity-5 rounded-full" data-id="element-7915" data-mp="352h8nusibxxf"></div>
            <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-white bg-opacity-5 rounded-full" data-id="element-7916" data-mp="3939jgsh12avt"></div>

            <div className="relative z-10" data-id="element-7917" data-mp="3e2mh889ft2d6">
              <div className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-medium mb-4" data-id="element-7918" data-mp="3j2s1329gtkkp">
                <SparklesIcon className="w-4 h-4 mr-2 text-yellow-300" data-id="element-7919" data-mp="3nwmi1xxorlg3" />
                Your Results Are Ready
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-7920" data-mp="q2l1777qxb8">
                {title} Plan
              </h1>
              <p className="text-blue-100 max-w-2xl mx-auto mb-8 text-lg" data-id="element-7921" data-mp="5nvnbp5eb768">
                Based on your answers, we've curated the perfect supplement
                stack to help you reach your goals faster.
              </p>

              <div className="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg" data-id="element-7922" data-mp="3k57jrquzyzgi">
                <TagIcon className="w-5 h-5 mr-2" data-id="element-7923" data-mp="1tkl9d46h2aa" />
                Enjoy 15% OFF your recommended stack!
              </div>
            </div>
          </div>

          <div className="mb-8" data-id="element-7924" data-mp="35985fyhao32x">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-7925" data-mp="35ajas2qe1iw5">
              Your Recommended Supplements
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7926" data-mp="3f6cvi9e9gip0">
              {recommendedProducts.map(product => <EnhancedProductCard key={product.id} product={product} data-id="element-7927" data-mp="3a6i321eu7l1u" />)}
            </div>
          </div>

          <div className="flex justify-center mt-12" data-id="element-7928" data-mp="ueu98ev4e8n9">
            <button onClick={() => navigate('/categories')} className="bg-[#1E2B55] text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-900 transition-colors flex items-center" data-id="element-7929" data-mp="phu17yepmvti">
              Shop All Supplements
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-7930" data-mp="2cbxl553tic95" />
            </button>
          </div>
        </main>

        <Footer data-id="element-7931" data-mp="1ygbwx05erwhs" />
        <MobileFooterNav data-id="element-7932" data-mp="23dc8fql11rga" />
        <PharmacistChat data-id="element-7933" data-mp="23ckbocfp1nof" />

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={loc => {
        setSelectedLocation(loc);
        setLocationModalOpen(false);
      }} data-id="element-7934" data-mp="23dvof1r4a5ro" />
      </div>
    </HelmetProvider>;
}